import React, { useEffect, useState } from "react"
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from "react-google-recaptcha-v3"
import {
  createSearchParams,
  Link,
  useNavigate,
  useSearchParams,
} from "react-router-dom"
import { observer } from "mobx-react"
import { Button, Col, Flex, Form, Input, Row, Typography } from "antd"
import { useForm } from "antd/es/form/Form"
import classNames from "classnames"

import { authStore, LoginReqType } from "@src/app/authStore"
import { sendGTagEvent } from "@src/app/components/GTagEvent"
import { configStore } from "@src/app/configStore"
import { AuthLayout } from "@src/app/layouts/AuthLayout"
import { paths } from "@src/app/paths"
import uiStore from "@src/app/uiStore"
import { InlineObject12 } from "@src/shared/api/generated"
import { checkPartner } from "@src/shared/checkPartner"
import { useBreakpoint } from "@src/shared/hooks/useBreakpoints"
import { useUserUtm } from "@src/shared/hooks/useUserUtm"
import { useFormErrors } from "@src/shared/useFormErrors"
import css from "./index.module.css"

const { Item } = Form

const { Title } = Typography

export const ReCaptchaProvider: React.FC<React.PropsWithChildren> = observer(
  ({ children }) => {
    const { config, isLoading } = configStore

    return (
      <GoogleReCaptchaProvider
        container={{
          parameters: {
            badge: "bottomright",
          },
        }}
        reCaptchaKey={isLoading ? "" : config?.re_captcha?.secret ?? ""}
      >
        {children}
      </GoogleReCaptchaProvider>
    )
  },
)

const AuthPageContent = observer(() => {
  useUserUtm()
  const { isLogging, login, merchant, sendConfirmationCode } = authStore
  const { isTransferioTheme } = uiStore()
  const { smallerThanLg } = useBreakpoint()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isChangeText] = useState(searchParams.get("changeText") !== null)
  const { catchFormErrors, getErrorMessage, resetErrors } = useFormErrors()
  const [errorCount, setErrorCount] = useState(0)
  const [submitCount, setSubmitCount] = useState(0)
  const referral_link_id = new URLSearchParams(window.location.search).get("id")
  const isSendCrypto = checkPartner(referral_link_id)
  const [form] = useForm()
  const { executeRecaptcha } = useGoogleReCaptcha()

  useEffect(() => {
    if (isChangeText) {
      searchParams.delete("changeText")
    }
  }, [isChangeText])

  const onSubmit = async (values: LoginReqType) => {
    try {
      if (!executeRecaptcha) {
        throw new Error("reCAPTCHA is not ready")
      }

      const re_captcha = await executeRecaptcha("login")

      return login({ ...values, re_captcha })
        .then(async (merch) => {
          await (!merch.is_confirmed
            ? sendConfirmationCode()
            : Promise.resolve())
          sendGTagEvent("login")

          return merch
        })
        .then((merch) => {
          if (merch.is_confirmed) {
            navigate({
              pathname: paths.default,
            })
          } else {
            navigate({
              pathname: paths.registrationConfirm,
              search: `?${createSearchParams({ email: merch.email ?? "" })}`,
            })
          }
        })
        .catch((error) => {
          setSubmitCount((state) => state + 1)

          catchFormErrors(error)
        })
    } catch (error: any) {
      setSubmitCount((state) => state + 1)
      catchFormErrors(error)
    }
  }

  const otpError = getErrorMessage("otp").hasError

  useEffect(() => {
    setErrorCount((state) => state + 1)
  }, [submitCount])

  return (
    <>
      <AuthLayout>
        <Flex
          className={css.form}
          style={{ padding: smallerThanLg ? 0 : 20, width: "100%" }}
          vertical
        >
          <Typography className={classNames([css.typography, "signin-title"])}>
            {isChangeText ? (
              <Flex gap={4} style={{ marginBottom: 16 }} vertical>
                <Title className={css.title} style={{ margin: 0 }}>
                  Get started!
                </Title>
                <Typography.Text className={css.subtitle}>
                  Sign In to your Cryptix account to see what's next
                </Typography.Text>
              </Flex>
            ) : (
              <Title className={css.title}>Sign in!</Title>
            )}
          </Typography>
          <Form
            className={css.formInner}
            form={form}
            id="GENERATED_ID_AUTH_FORM_VOID_1"
            layout="vertical"
            name="auth"
            onFinish={onSubmit}
            size="middle"
            style={{
              padding: smallerThanLg ? "32px 16px" : 40,
              width: "100%",
            }}
          >
            <Item
              extra={getErrorMessage("email").message}
              label="Email"
              name="email"
              rules={[{ message: "Please input your email", required: true }]}
            >
              <Input />
            </Item>

            <Item
              label="Password"
              name="password"
              rules={[
                {
                  message: "Please input your password",
                  min: 8,
                  required: true,
                },
              ]}
            >
              <Input.Password />
            </Item>

            {otpError && (
              <Item
                extra={errorCount > 2 && getErrorMessage("otp").message}
                label="OTP"
                name="otp"
                required
              >
                <Input />
              </Item>
            )}

            <Item>
              <Button
                className={css.button}
                htmlType="submit"
                id="GENERATED_ID_AUTH_BUTTON_LOGIN_1"
                loading={isLogging}
                type="primary"
              >
                Sign in
              </Button>
            </Item>
          </Form>
          <div>
            Don’t have an account?{" "}
            <Link
              className={css.link}
              id="GENERATED_ID_AUTH_LINK_SIGN_UP_FOR_1"
              to={
                referral_link_id
                  ? `${paths.registration}?id=${referral_link_id}`
                  : paths.registration
              }
            >
              Sign up for free
            </Link>
          </div>

          <div className={css.recover}>
            Forgot password?{" "}
            <Link
              className={css.link}
              id="GENERATED_ID_AUTH_LINK_CLICK_HERE_1"
              to={paths.recover}
            >
              Click here
            </Link>
          </div>
        </Flex>
      </AuthLayout>
    </>
  )
})

export const AuthPage = () => (
  <ReCaptchaProvider>
    <AuthPageContent />
  </ReCaptchaProvider>
)
